import React from 'react';
import { User, Bot, AlertCircle } from 'lucide-react';
import './Message.css';

const Message = ({ message }) => {
  const { text, sender, timestamp, isError, securityWarning } = message;
  const isUser = sender === 'user';
  
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className={`message ${isUser ? 'user-message' : 'bot-message'} ${isError ? 'error-message' : ''}`}>
      <div className="message-avatar">
        {isUser ? (
          <User size={20} />
        ) : isError ? (
          <AlertCircle size={20} />
        ) : (
          <Bot size={20} />
        )}
      </div>
      
      <div className="message-content">
        <div className="message-bubble">
          <p className="message-text">{text}</p>
        </div>
        {securityWarning && (
          <div className={`security-warning severity-${securityWarning.severity}`}>
            <div className="bltz-shield-header">
              <div className="shield-left">
                <img src="/shield_still.png" alt="Shield" className="shield-icon" width="20" />
                <span className="bltz-shield-title">Bltz Shield terminiated an unsafe input!</span>
              </div>
              <div className="severity-badge-container">
                <span className="security-label">Severity:</span>
                <span className={`security-severity ${securityWarning.severity}`}>
                  {securityWarning.severity}
                </span>
              </div>
            </div>
            <div className="security-details">
              <div className="security-field">
                <span className="security-explanation">{securityWarning.explanation}</span>
              </div>
              {securityWarning.detection_category && (
                <div className="category-section">
                  <div className="security-field detection-field">
                    <span className="security-label">Detection Category:</span>
                    <span className={`security-explanation detection-value ${securityWarning.severity}`}>{securityWarning.detection_category}</span>
                    <span className="security-label"><span className="detection-label">Detection:</span></span>
                    <span className={`security-explanation detection-value ${securityWarning.severity}`}>{securityWarning.detection}</span>
                  </div>
                </div>
              )}
              <div className="mapping-section owasp-section">
                <div className="security-header">
                  <img src="/owasp_dark.png" alt="OWASP" className="owasp-icon" />
                  <span className="security-title">
                    OWASP LLM Top 10: {securityWarning.owasp_id}
                    {securityWarning.owasp_name && ` - ${securityWarning.owasp_name}`}
                  </span>
                </div>
              </div>
            </div>

            {/* NIST AI RMF Mappings */}
            {securityWarning.nist_ai_rmf_mappings && securityWarning.nist_ai_rmf_mappings.length > 0 && (
              <div className="mapping-section">
                <div className="mapping-header">
                  <img src="/nisrmf.png" alt="NIST AI RMF" className="mapping-icon" />
                  <span className="mapping-title">NIST AI RMF</span>
                </div>
                <div className="mapping-list">
                  <div className="mapping-column-headers">
                    <span className="column-header col1">Function</span>
                    <span className="column-header col2">ID</span>
                    <span className="column-header col3">Category</span>
                  </div>
                  {securityWarning.nist_ai_rmf_mappings.map((mapping, index) => (
                    <div key={index} className="mapping-item">
                      <span className="mapping-function col1">{mapping.function}</span>
                      <span className="mapping-id col2">{mapping.category_id}</span>
                      <span className="mapping-name col3">{mapping.category_name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* MITRE ATLAS Mappings */}
            {securityWarning.mitre_atlas_mappings && securityWarning.mitre_atlas_mappings.length > 0 && (
              <div className="mapping-section">
                <div className="mapping-header">
                  <img src="/mitreatlas_dark.png" alt="MITRE ATLAS" className="mapping-icon" />
                  <span className="mapping-title">MITRE ATLAS</span>
                </div>
                <div className="mapping-list">
                  <div className="mapping-column-headers">
                    <span className="column-header col1">Tactic</span>
                    <span className="column-header col2">ID</span>
                    <span className="column-header col3">Description</span>
                  </div>
                  {securityWarning.mitre_atlas_mappings.map((mapping, index) => (
                    <div key={index} className="mapping-item">
                      <span className="mapping-name col1">{mapping.tactic_name}</span>
                      <span className="mapping-id col2">{mapping.aml_id}</span>
                      <span className="mapping-description col3">{mapping.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ISO 42001 Mappings */}
            {securityWarning.iso_42001_mappings && securityWarning.iso_42001_mappings.length > 0 && (
              <div className="mapping-section">
                <div className="mapping-header">
                  <img src="/iso42001.png" alt="ISO 42001" className="mapping-icon" />
                  <span className="mapping-title">ISO 42001</span>
                </div>
                <div className="mapping-list">
                  <div className="mapping-column-headers">
                    <span className="column-header col1">Control</span>
                    <span className="column-header col2">ID</span>
                    <span className="column-header col3">Description</span>
                  </div>
                  {securityWarning.iso_42001_mappings.map((mapping, index) => (
                    <div key={index} className="mapping-item">
                      <span className="mapping-name col1">{mapping.control_name}</span>
                      <span className="mapping-id col2">{mapping.control_id}</span>
                      <span className="mapping-description col3">{mapping.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <span className="message-time">{formatTime(timestamp)}</span>
      </div>
    </div>
  );
};

export default Message;